import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { NSSelect } from '../shared/ns-kit'
import {
  MessageSquare,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Search,
  RotateCcw,
  Image as ImageIcon,
  Send,
  FileText,
  Zap,
  AlertTriangle,
  X,
  RefreshCw,
  ExternalLink,
  Building,
  MessageCircle,
  Reply,
  Edit3,
  ChevronRight,
  ChevronLeft,
  Flag,
  Activity,
  Headphones,
  Circle,
  Eye,
  Trash2
} from 'lucide-react'

const SupportManagement = () => {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const isInitialLoad = React.useRef(true)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' })
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const isInitialMount = React.useRef(true)

  const statusConfig = {
    open: { badgeClass: 'badge-accent', icon: AlertCircle, label: 'Open' },
    in_progress: { badgeClass: 'badge-warning', icon: Clock, label: 'In Progress' },
    resolved: { badgeClass: 'badge-success', icon: CheckCircle, label: 'Resolved' },
    closed: { badgeClass: '', icon: XCircle, label: 'Closed' }
  }

  const priorityConfig = {
    low: { color: 'var(--success)', label: 'Low', bg: 'var(--success-soft)' },
    normal: { color: 'var(--accent)', label: 'Normal', bg: 'var(--accent-soft)' },
    high: { color: 'var(--warning)', label: 'High', bg: 'var(--warning-soft)' },
    urgent: { color: 'var(--danger)', label: 'Urgent', bg: 'var(--danger-soft)' }
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      fetchMessages()
      return
    }
    const isFilterChange = filters.search.length > 0
    if (isFilterChange) {
      const timeoutId = setTimeout(() => fetchMessages(), 500)
      return () => clearTimeout(timeoutId)
    } else {
      fetchMessages()
    }
  }, [pagination.page, filters, sortBy, sortOrder])

  const fetchMessages = async () => {
    try {
      if (isInitialLoad.current) setLoading(true)
      const queryParams = new URLSearchParams({ page: pagination.page, limit: pagination.limit, ...filters })
      const response = await apiService.get(`/support?${queryParams}`)
      const data = response.data
      setMessages(data.messages)
      setPagination(prev => ({ ...prev, total: data.pagination.total, pages: data.pagination.pages }))
    } catch (error) {
      console.error('Error fetching support messages:', error)
    } finally {
      if (isInitialLoad.current) {
        setLoading(false)
        isInitialLoad.current = false
      }
    }
  }

  const updateMessageStatus = async (messageId, updates) => {
    try {
      await apiService.put(`/support/${messageId}`, updates)
      fetchMessages()
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage({ ...selectedMessage, ...updates })
      }
    } catch (error) {
      console.error('Error updating message:', error)
    }
  }

  const getMessageDetails = async (messageId) => {
    try {
      const response = await apiService.get(`/support/${messageId}`)
      setSelectedMessage(response.data)
    } catch (error) {
      console.error('Error fetching message details:', error)
    }
  }

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) getMessageDetails(openId)
  }, [searchParams])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diff = now - time
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return time.toLocaleDateString()
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <Zap style={{ width: 14, height: 14, color: 'var(--danger)' }} />
      case 'high': return <AlertTriangle style={{ width: 14, height: 14, color: 'var(--warning)' }} />
      case 'normal': return <Flag style={{ width: 14, height: 14, color: 'var(--accent)' }} />
      case 'low': return <Circle style={{ width: 14, height: 14, color: 'var(--success)' }} />
      default: return <Flag style={{ width: 14, height: 14, color: 'var(--accent)' }} />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading support tickets..." />
      </div>
    )
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.1 } }
  }
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }

  return (
    <motion.div
      style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 24 }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
      >
        <div>
          <h1 className="h2">Support Management</h1>
          <p className="text-muted" style={{ fontSize: 13 }}>Manage and respond to client support requests</p>
        </div>
        <button className="btn" onClick={() => fetchMessages()}>
          <RefreshCw style={{ width: 16, height: 16, marginRight: 8 }} />Refresh
        </button>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={itemVariants} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 24 }}>
        {[
          { label: 'Total Tickets', value: pagination.total || 0, bg: 'var(--accent)', icon: <MessageSquare style={{ width: 22, height: 22, color: '#fff' }} /> },
          { label: 'Open Tickets', value: messages.filter(m => m.status === 'open').length, bg: 'var(--warning)', icon: <AlertCircle style={{ width: 22, height: 22, color: '#fff' }} /> },
          { label: 'Resolved', value: messages.filter(m => m.status === 'resolved').length, bg: 'var(--success)', icon: <CheckCircle style={{ width: 22, height: 22, color: '#fff' }} /> },
          { label: 'Urgent', value: messages.filter(m => m.priority === 'urgent').length, bg: 'var(--danger)', icon: <Zap style={{ width: 22, height: 22, color: '#fff' }} /> },
        ].map((stat, i) => (
          <div key={i} className="card card-pad">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-soft" style={{ fontSize: 13 }}>{stat.label}</p>
                <p className="font-bold" style={{ fontSize: 24 }}>{stat.value}</p>
              </div>
              <div style={{ width: 48, height: 48, background: stat.bg, borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
              <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input"
                style={{ paddingLeft: 32, fontSize: 13 }}
              />
            </div>
            <NSSelect
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ minWidth: 140, width: 140, flexShrink: 0 }}
              options={[
                { value: '', label: 'All Statuses' },
                ...Object.entries(statusConfig).map(([value, config]) => ({ value, label: config.label })),
              ]}
            />
            <NSSelect
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              style={{ minWidth: 140, width: 140, flexShrink: 0 }}
              options={[
                { value: '', label: 'All Priorities' },
                ...Object.entries(priorityConfig).map(([value, config]) => ({ value, label: config.label })),
              ]}
            />
            <button
              className="btn btn-sm"
              onClick={() => { setFilters({ status: '', priority: '', search: '' }); setPagination(prev => ({ ...prev, page: 1 })) }}
            >
              <RotateCcw style={{ width: 14, height: 14, marginRight: 6 }} />Clear
            </button>
          </div>
        </div>
      </motion.div>

      {/* Tickets List */}
      <motion.div variants={itemVariants}>
        {messages.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, margin: '0 auto 16px', background: 'var(--surface-2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Headphones style={{ width: 28, height: 28, color: 'var(--text-muted)' }} />
            </div>
            <h3 className="font-medium" style={{ marginBottom: 6 }}>No support tickets</h3>
            <p className="text-soft" style={{ fontSize: 13, maxWidth: 400, margin: '0 auto' }}>
              {Object.values(filters).some(f => f) ? 'No tickets match your current filters.' : 'No support tickets found.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((message) => {
              const StatusIcon = statusConfig[message.status]?.icon || AlertCircle
              const isUnread = !message.is_read
              const pc = priorityConfig[message.priority]

              return (
                <div
                  key={message.id}
                  onClick={() => getMessageDetails(message.id)}
                  style={{
                    padding: 16, border: `1px solid ${isUnread ? 'var(--border-accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'all 0.2s',
                    background: isUnread ? 'var(--accent-soft)' : 'var(--surface)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 0 }}>
                      {/* Priority indicator */}
                      <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: pc?.bg }}>
                        {getPriorityIcon(message.priority)}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 className="font-semibold truncate" style={{ fontSize: 14, marginBottom: 4 }}>{message.subject}</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                          <span className="flex items-center gap-1"><Building style={{ width: 11, height: 11 }} />{message.company_name}</span>
                          <span className="flex items-center gap-1"><User style={{ width: 11, height: 11 }} />{message.user_name}</span>
                          <span className="flex items-center gap-1"><Calendar style={{ width: 11, height: 11 }} />{getTimeAgo(message.created_at)}</span>
                          {message.screenshot_url && <span className="flex items-center gap-1"><ImageIcon style={{ width: 11, height: 11 }} />Screenshot</span>}
                        </div>
                        <p className="text-soft" style={{ fontSize: 13, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{message.message}</p>
                      </div>
                    </div>
                    {/* Status + Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span className={`badge ${statusConfig[message.status]?.badgeClass || ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <StatusIcon style={{ width: 10, height: 10 }} />
                          {statusConfig[message.status]?.label}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: pc?.bg, color: pc?.color }}>
                          {priorityConfig[message.priority]?.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {message.status !== 'resolved' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => { e.stopPropagation(); updateMessageStatus(message.id, { status: 'resolved' }) }}
                            style={{ color: 'var(--success)', fontSize: 12 }}
                          >
                            <CheckCircle style={{ width: 14, height: 14, marginRight: 4 }} />Resolve
                          </button>
                        )}
                        {message.status !== 'in_progress' && message.status !== 'resolved' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => { e.stopPropagation(); updateMessageStatus(message.id, { status: 'in_progress' }) }}
                            style={{ color: 'var(--warning)', fontSize: 12 }}
                          >
                            <Clock style={{ width: 14, height: 14, marginRight: 4 }} />Progress
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => getMessageDetails(message.id)}
                          style={{ color: 'var(--accent)', fontSize: 12 }}
                        >
                          <Eye style={{ width: 14, height: 14, marginRight: 4 }} />View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ marginTop: 12, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <p className="text-soft" style={{ fontSize: 13 }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} tickets
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                <ChevronLeft style={{ width: 14, height: 14, marginRight: 4 }} />Previous
              </button>
              <span className="text-soft" style={{ fontSize: 13 }}>Page {pagination.page} of {pagination.pages}</span>
              <button
                className="btn btn-sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
              >
                Next<ChevronRight style={{ width: 14, height: 14, marginLeft: 4 }} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageDetailModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onUpdate={updateMessageStatus}
          onDelete={async (id) => {
            try {
              await apiService.delete(`/support/${id}`)
              setSelectedMessage(null)
              fetchMessages()
            } catch (error) {
              console.error('Error deleting ticket:', error)
            }
          }}
        />
      )}
    </motion.div>
  )
}

const MessageDetailModal = ({ message, onClose, onUpdate, onDelete }) => {
  const [response, setResponse] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [replies, setReplies] = useState([])
  const [loadingReplies, setLoadingReplies] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    const fetchReplies = async () => {
      try {
        setLoadingReplies(true)
        const res = await apiService.get(`/support/${message.id}/replies`)
        setReplies(res.data || [])
      } catch (error) {
        console.error('Error fetching replies:', error)
      } finally {
        setLoadingReplies(false)
      }
    }
    fetchReplies()
  }, [message.id])

  const statusConfig = {
    open: { badgeClass: 'badge-accent', icon: AlertCircle, label: 'Open' },
    in_progress: { badgeClass: 'badge-warning', icon: Clock, label: 'In Progress' },
    resolved: { badgeClass: 'badge-success', icon: CheckCircle, label: 'Resolved' },
    closed: { badgeClass: '', icon: XCircle, label: 'Closed' }
  }

  const priorityConfig = {
    low: { color: 'var(--success)', label: 'Low', bg: 'var(--success-soft)' },
    normal: { color: 'var(--accent)', label: 'Normal', bg: 'var(--accent-soft)' },
    high: { color: 'var(--warning)', label: 'High', bg: 'var(--warning-soft)' },
    urgent: { color: 'var(--danger)', label: 'Urgent', bg: 'var(--danger-soft)' }
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <Zap style={{ width: 12, height: 12, color: 'var(--danger)' }} />
      case 'high': return <AlertTriangle style={{ width: 12, height: 12, color: 'var(--warning)' }} />
      case 'low': return <Circle style={{ width: 12, height: 12, color: 'var(--success)' }} />
      default: return <Flag style={{ width: 12, height: 12, color: 'var(--accent)' }} />
    }
  }

  const handleSendReply = async (resolve = false) => {
    if (!response.trim()) return
    setIsSubmitting(true)
    try {
      await apiService.post(`/support/${message.id}/replies`, { message: response.trim() })
      toast.success(resolve ? 'Reply sent & ticket resolved' : 'Reply sent')
      if (resolve) {
        await onUpdate(message.id, { status: 'resolved' })
        onClose()
      } else {
        const res = await apiService.get(`/support/${message.id}/replies`)
        setReplies(res.data || [])
        setResponse('')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reply')
    } finally {
      setIsSubmitting(false)
    }
  }

  const StatusIcon = statusConfig[message.status]?.icon || AlertCircle
  const pc = priorityConfig[message.priority]

  return (
    <div className="modal-backdrop">
      <motion.div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />
      <motion.div
        style={{
          position: 'relative', width: '100%', maxWidth: 720, margin: 'auto',
          background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow)', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--border)'
        }}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="font-semibold truncate" style={{ fontSize: 16, lineHeight: 1.3 }}>{message.subject}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                <span className="flex items-center gap-1"><Building style={{ width: 12, height: 12 }} />{message.company_name}</span>
                <span className="flex items-center gap-1"><User style={{ width: 12, height: 12 }} />{message.user_name}</span>
                <span className="flex items-center gap-1"><Calendar style={{ width: 12, height: 12 }} />{new Date(message.created_at).toLocaleString()}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span className={`badge ${statusConfig[message.status]?.badgeClass || ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <StatusIcon style={{ width: 10, height: 10 }} />
                {statusConfig[message.status]?.label}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: pc?.bg, color: pc?.color }}>
                {getPriorityIcon(message.priority)}
                {priorityConfig[message.priority]?.label}
              </span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 24, padding: 24 }}>
            {/* Left: conversation */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Original message */}
              <div>
                <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MessageCircle style={{ width: 12, height: 12 }} /> Message
                </p>
                <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', padding: 16, border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 13, color: 'var(--text-soft)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{message.message}</p>
                </div>
              </div>

              {/* Screenshot */}
              {message.screenshot_url && (
                <a
                  href={message.screenshot_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--accent-soft)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}
                >
                  <ImageIcon style={{ width: 14, height: 14 }} />
                  <span className="font-medium">View attachment</span>
                  <ExternalLink style={{ width: 12, height: 12 }} />
                </a>
              )}

              {/* Conversation */}
              {(message.response_message || replies.length > 0 || !loadingReplies) && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MessageCircle style={{ width: 12, height: 12 }} /> Conversation
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {message.response_message && replies.length === 0 && (
                      <div style={{ background: 'var(--success-soft)', borderRadius: 'var(--radius-lg)', padding: 14, border: '1px solid var(--success)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--success)' }}>Support Team</span>
                          {message.responded_at && <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{new Date(message.responded_at).toLocaleString()}</span>}
                        </div>
                        <p style={{ fontSize: 13, color: 'var(--text-soft)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{message.response_message}</p>
                      </div>
                    )}
                    {loadingReplies ? (
                      <p style={{ fontSize: 11, color: 'var(--text-faint)', padding: '8px 0' }}>Loading replies…</p>
                    ) : replies.length === 0 && !message.response_message ? (
                      <p style={{ fontSize: 11, color: 'var(--text-faint)', padding: '8px 0', fontStyle: 'italic' }}>No replies yet.</p>
                    ) : (
                      replies.map(reply => (
                        <div key={reply.id} style={{
                          borderRadius: 'var(--radius-lg)', padding: 14,
                          background: reply.role === 'admin' ? 'var(--success-soft)' : 'var(--surface-2)',
                          border: `1px solid ${reply.role === 'admin' ? 'var(--success)' : 'var(--border)'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 500, color: reply.role === 'admin' ? 'var(--success)' : 'var(--text-soft)' }}>
                              {reply.role === 'admin' ? 'Support Team' : reply.user_name}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{new Date(reply.created_at).toLocaleString()}</span>
                          </div>
                          <p style={{ fontSize: 13, color: 'var(--text-soft)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{reply.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Reply form */}
              {message.status !== 'closed' && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Edit3 style={{ width: 12, height: 12 }} /> Reply
                  </p>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Type your response to the client..."
                    rows={4}
                    className="input"
                    style={{ resize: 'none', fontSize: 13 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn btn-sm" onClick={() => onUpdate(message.id, { status: 'in_progress' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Clock style={{ width: 12, height: 12 }} />In Progress
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => handleSendReply(false)}
                      disabled={!response.trim() || isSubmitting}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <Send style={{ width: 12, height: 12 }} />
                      {isSubmitting ? 'Sending…' : 'Send Update'}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSendReply(true)}
                      disabled={!response.trim() || isSubmitting}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <CheckCircle style={{ width: 12, height: 12 }} />
                      {isSubmitting ? 'Sending…' : 'Send & Resolve'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: management panel */}
            <div style={{ width: 180, flexShrink: 0 }}>
              <div style={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8 }}>Status</p>
                  <NSSelect
                    value={message.status}
                    onChange={(e) => onUpdate(message.id, { status: e.target.value })}
                    options={Object.entries(statusConfig).map(([value, config]) => ({ value, label: config.label }))}
                  />
                </div>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8 }}>Priority</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, background: pc?.bg, color: pc?.color }}>
                    {getPriorityIcon(message.priority)}
                    {priorityConfig[message.priority]?.label}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8 }}>Contact</p>
                  <p className="font-medium" style={{ fontSize: 13 }}>{message.user_name}</p>
                  <p className="text-muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>{message.user_email}</p>
                </div>
                <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  {confirmDelete ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <p style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 500 }}>Delete this ticket?</p>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-danger btn-sm" style={{ flex: 1, fontSize: 11 }} onClick={() => onDelete(message.id)}>Yes, delete</button>
                        <button className="btn btn-sm" style={{ flex: 1, fontSize: 11 }} onClick={() => setConfirmDelete(false)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn btn-ghost btn-sm w-full"
                      onClick={() => setConfirmDelete(true)}
                      style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                    >
                      <Trash2 style={{ width: 12, height: 12 }} />Delete ticket
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SupportManagement
